import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { useAllWards, usePermission } from '../hooks';
import { revenueService } from '../services';
import { colors } from '../theme/theme';

export const RevenueTargetsPage = () => {
  const { can } = usePermission();
  const { streamId } = useParams<{ streamId: string }>();
  const navigate = useNavigate();
  const [targets, setTargets] = useState<{ [wardId: number]: number }>({});
  const [collections, setCollections] = useState<{ [wardId: number]: number }>({});
  const [streamCollected, setStreamCollected] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [currentStream, setCurrentStream] = useState<any>(null);
  const { wards, isLoading: wardsLoading } = useAllWards();
  const [streamLoading, setStreamLoading] = useState(true);
  const [bulkAmount, setBulkAmount] = useState('');

  // Load stream, existing targets, and ward collections
  useEffect(() => {
    const loadData = async () => {
      if (!streamId) return;

      try {
        setStreamLoading(true);
        const [streamRes, targetsRes, collectionsRes, summaryRes] = await Promise.all([
          revenueService.getRevenueStream(parseInt(streamId)),
          revenueService.getRevenueTargets(parseInt(streamId)),
          revenueService.getWardCollections(parseInt(streamId)),
          revenueService.getStreamSummary(parseInt(streamId)),
        ]);

        if (streamRes.status === 'success' && streamRes.data) {
          setCurrentStream(streamRes.data);
        }

        if (summaryRes.status === 'success' && summaryRes.data) {
          setStreamCollected(parseFloat((summaryRes.data as any).collected) || 0);
        }

        const existingTargets: { [wardId: number]: number } = {};
        const targetsData = (targetsRes as any).data;
        const targetsList = Array.isArray(targetsData) ? targetsData : (targetsData?.data ?? []);
        targetsList.forEach((target: any) => {
          if (target.ward_id) {
            existingTargets[target.ward_id] = parseFloat(target.target_amount) || 0;
          }
        });
        setTargets(existingTargets);

        if (collectionsRes.status === 'success' && collectionsRes.data) {
          const rawCollections = collectionsRes.data as any;
          const collectionsMap: { [wardId: number]: number } = {};
          if (typeof rawCollections === 'object') {
            Object.entries(rawCollections).forEach(([key, val]) => {
              collectionsMap[parseInt(key)] = parseFloat(val as string) || 0;
            });
          }
          setCollections(collectionsMap);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setStreamLoading(false);
      }
    };

    loadData();
  }, [streamId]);

  const handleTargetChange = (wardId: number, value: string) => {
    const numValue = parseFloat(value) || 0;
    setTargets((prev) => ({ ...prev, [wardId]: numValue }));
  };

  const calculateTotal = () => {
    return Object.values(targets).reduce((sum, value) => sum + value, 0);
  };

  const calculateTotalCollected = () => {
    return Object.values(collections).reduce((sum, value) => sum + value, 0);
  };

  const formatCurrency = (amount: number) => {
    return `KES ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 85) return colors.success.main;
    if (progress >= 70) return colors.warning.main;
    return colors.error.main;
  };

  const handleSaveTargets = async () => {
    if (!streamId) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const promises = Object.entries(targets).map(([wardId, amount]) => {
        if (amount > 0) {
          return revenueService.createRevenueTarget({
            revenue_stream_id: parseInt(streamId),
            ward_id: parseInt(wardId),
            target_amount: amount,
          });
        }
        return Promise.resolve();
      });

      await Promise.all(promises);
      setSaveSuccess(true);
      setTimeout(() => {
        navigate(`/finance/streams/${currentStream?.financial_period_id || currentStream?.financial_period?.id}`);
      }, 1500);
    } catch (err) {
      console.error('Failed to save targets:', err);
      setSaveError('Failed to save targets. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (streamLoading || wardsLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  const totalTarget = calculateTotal();
  const wardCollectedTotal = calculateTotalCollected();
  // Use stream-level collected (from bills.service_id) as primary, ward-level as breakdown
  const totalCollected = streamCollected > 0 ? streamCollected : wardCollectedTotal;
  const overallProgress = totalTarget > 0 ? (totalCollected / totalTarget) * 100 : 0;
  const isPeriodClosed = currentStream?.financial_period?.status === 'closed' || currentStream?.financialPeriod?.status === 'closed';

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title={`Revenue Targets - ${currentStream?.name || 'Stream'}`}
          subtitle={`${currentStream?.financial_period?.name || currentStream?.financialPeriod?.name || 'Financial Period'}`}
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Finance & Budget', path: '/finance/dashboard' },
            { label: 'Financial Periods', path: '/financial-periods' },
            {
              label: 'Revenue Streams',
              path: `/finance/streams/${currentStream?.financial_period_id}`
            },
            { label: 'Targets' },
          ]}
        />

        {saveError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {saveError}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Targets saved successfully! Redirecting...
          </Alert>
        )}

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total Target</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: colors.primary.main }}>
                {formatCurrency(totalTarget)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total Collected</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: colors.success.main }}>
                {formatCurrency(totalCollected)}
              </Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: 1, minWidth: 200 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Overall Progress</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: getProgressColor(overallProgress) }}>
                {overallProgress.toFixed(1)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(overallProgress, 100)}
                sx={{
                  mt: 1, height: 6, borderRadius: 1, bgcolor: colors.grey[200],
                  '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(overallProgress) },
                }}
              />
            </CardContent>
          </Card>
        </Box>

        <Card>
          <CardContent>
            {/* Bulk Assignment */}
            {!isPeriodClosed && <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, p: 2, bgcolor: colors.grey[50], borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>Set all wards to:</Typography>
              <TextField
                type="number"
                size="small"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                placeholder="Amount (KES)"
                inputProps={{ step: '0.01', min: '0' }}
                sx={{ maxWidth: 200 }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={!bulkAmount || !wards?.data?.length}
                onClick={() => {
                  const amount = parseFloat(bulkAmount) || 0;
                  if (amount > 0 && wards?.data) {
                    const newTargets: { [wardId: number]: number } = {};
                    wards.data.forEach((ward: any) => { newTargets[ward.id] = amount; });
                    setTargets(newTargets);
                    setBulkAmount('');
                  }
                }}
              >
                Apply to All
              </Button>
              <Button
                variant="text"
                size="small"
                color="error"
                onClick={() => {
                  if (wards?.data) {
                    const cleared: { [wardId: number]: number } = {};
                    wards.data.forEach((ward: any) => { cleared[ward.id] = 0; });
                    setTargets(cleared);
                  }
                }}
              >
                Clear All
              </Button>
            </Box>}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600 }}>Ward</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Target Amount (KES)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Collected (KES)</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 200 }}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wards?.data?.map((ward: any) => {
                    const target = targets[ward.id] || 0;
                    const collected = collections[ward.id] || 0;
                    const progress = target > 0 ? (collected / target) * 100 : 0;
                    const progressColor = getProgressColor(progress);

                    return (
                      <TableRow key={ward.id} hover>
                        <TableCell>{ward.name}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={target || 0}
                            onChange={(e) => handleTargetChange(ward.id, e.target.value)}
                            inputProps={{ step: '0.01', min: '0' }}
                            disabled={isPeriodClosed}
                            fullWidth
                            sx={{ maxWidth: 250 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: collected > 0 ? colors.success.main : 'text.secondary' }}>
                            {formatCurrency(collected)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {target > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(progress, 100)}
                                sx={{
                                  flex: 1, height: 8, borderRadius: 1, bgcolor: colors.grey[200],
                                  '& .MuiLinearProgress-bar': { bgcolor: progressColor },
                                }}
                              />
                              <Chip
                                label={`${progress.toFixed(1)}%`}
                                size="small"
                                sx={{
                                  fontWeight: 600, fontSize: '0.75rem', minWidth: 60,
                                  bgcolor: `${progressColor}15`, color: progressColor,
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">No target set</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {(!wards?.data || wards.data.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No wards found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" gap={2} sx={{ mt: 3 }}>
              {!isPeriodClosed && can('finance.manage_targets') && (
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveTargets}
                  disabled={isSaving || calculateTotal() === 0}
                >
                  {isSaving ? 'Saving...' : 'Save Targets'}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => navigate(`/finance/streams/${currentStream?.financial_period_id || currentStream?.financial_period?.id}`)}
                disabled={isSaving}
              >
                {isPeriodClosed ? 'Back' : 'Cancel'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
};
